import type { PerformanceMetric } from '../types/analytics';

export interface PerformanceObserverLike {
  observe(options: { type: string; buffered?: boolean }): void;
  disconnect(): void;
}

export interface PerformanceTrackerDeps {
  now?: () => number;
  createPerformanceObserver?: (
    callback: (entries: { name: string; value?: number; startTime?: number; duration?: number }[]) => void,
  ) => PerformanceObserverLike | null;
  getMemory?: () => { usedJSHeapSize: number; totalJSHeapSize: number } | null;
}

export class PerformanceTracker {
  private fpsValues: number[] = [];
  private loadTimes: Map<string, number[]> = new Map();
  private longTasks: number[] = [];
  private webVitals: Map<string, number> = new Map();
  private metrics: PerformanceMetric[] = [];
  private deps: PerformanceTrackerDeps;
  private observer: PerformanceObserverLike | null = null;

  constructor(deps?: PerformanceTrackerDeps) {
    this.deps = deps ?? {};
  }

  startWebVitalsObserver(): void {
    if (!this.deps.createPerformanceObserver) return;

    this.observer = this.deps.createPerformanceObserver((entries) => {
      for (const entry of entries) {
        if (entry.value !== undefined) {
          this.webVitals.set(entry.name, entry.value);
        } else if (entry.startTime !== undefined) {
          this.webVitals.set(entry.name, entry.startTime);
        }
      }
    });

    if (this.observer) {
      try {
        this.observer.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch { /* unsupported */ }
      try {
        this.observer.observe({ type: 'first-input', buffered: true });
      } catch { /* unsupported */ }
      try {
        this.observer.observe({ type: 'layout-shift', buffered: true });
      } catch { /* unsupported */ }
    }
  }

  trackFPS(fps: number): void {
    this.fpsValues.push(fps);
    const now = this.deps.now?.() ?? Date.now();
    this.metrics.push({ name: 'fps', value: fps, unit: 'fps', timestamp: now });
  }

  trackLoadTime(assetName: string, duration: number): void {
    const existing = this.loadTimes.get(assetName) ?? [];
    existing.push(duration);
    this.loadTimes.set(assetName, existing);

    const now = this.deps.now?.() ?? Date.now();
    this.metrics.push({ name: `load_${assetName}`, value: duration, unit: 'ms', timestamp: now });
  }

  trackMemoryUsage(): PerformanceMetric | null {
    const memory = this.deps.getMemory?.();
    if (!memory) return null;

    const now = this.deps.now?.() ?? Date.now();
    const metric: PerformanceMetric = {
      name: 'memory_used',
      value: memory.usedJSHeapSize,
      unit: 'bytes',
      timestamp: now,
    };
    this.metrics.push(metric);
    return metric;
  }

  trackLongTask(duration: number): void {
    this.longTasks.push(duration);
    const now = this.deps.now?.() ?? Date.now();
    this.metrics.push({ name: 'long_task', value: duration, unit: 'ms', timestamp: now });
  }

  getAverages(): { avgFPS: number; avgLoadTime: number; p95LoadTime: number } {
    const avgFPS = this.fpsValues.length > 0
      ? this.fpsValues.reduce((a, b) => a + b, 0) / this.fpsValues.length
      : 0;

    const allLoadTimes = Array.from(this.loadTimes.values()).flat();
    const avgLoadTime = allLoadTimes.length > 0
      ? allLoadTimes.reduce((a, b) => a + b, 0) / allLoadTimes.length
      : 0;

    const p95LoadTime = this.computeP95(allLoadTimes);

    return { avgFPS, avgLoadTime, p95LoadTime };
  }

  getWebVitals(): Map<string, number> {
    return new Map(this.webVitals);
  }

  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  destroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.fpsValues = [];
    this.loadTimes.clear();
    this.longTasks = [];
    this.webVitals.clear();
    this.metrics = [];
  }

  private computeP95(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const idx = Math.ceil(sorted.length * 0.95) - 1;
    return sorted[Math.max(0, idx)];
  }
}
